import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { NativeVideoPlaceholderProvider, type NativeVideoPlaceholderProps } from 'pages/Meetings/call/custom/callTransport';
import { cn } from 'lib/utils';
import { nativeCall, type NativeCallState, type NativeVideoTile } from './nativeBridge';
import { acknowledgeNativeViewport, currentViewportRevision, nextViewportRevision, onNativeViewportRefresh, viewportScopeRetired, type NativeViewportReceipt } from './nativeVideoViewport';
import './nativeVideoTiles.css';

/** While set, the page is transparent over each placeholder so the native video beneath shows through. */
export const NATIVE_VIDEO_BEHIND_CLASS = 'wf-native-video-behind';

/** The shell rejects a viewport with more tiles than this (CallBridgePolicy.MAX_VIDEO_TILES). */
const MAX_NATIVE_VIDEO_TILES = 25;

type Placeholder = { sessionId: string; type: 'video' | 'screenVideo'; fit: 'cover' | 'contain' };
type Registry = { register: (element: HTMLElement, placeholder: Placeholder) => () => void };
const RegistryContext = createContext<Registry | null>(null);

const intersect = (a: DOMRect | { left: number; top: number; right: number; bottom: number }, b: { left: number; top: number; right: number; bottom: number }) => ({
  left: Math.max(a.left, b.left), top: Math.max(a.top, b.top), right: Math.min(a.right, b.right), bottom: Math.min(a.bottom, b.bottom),
});

/** The tile's rectangle and the part of it its scrolling/clipping ancestors leave visible. */
export function measureNativeVideoTile(element: HTMLElement, placeholder: Placeholder): NativeVideoTile | null {
  const rect = element.getBoundingClientRect();
  if (!(rect.width > 0 && rect.height > 0) || ![rect.x, rect.y, rect.width, rect.height].every(Number.isFinite)) return null;
  let clip = intersect(rect, { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight });
  for (let parent = element.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    const style = window.getComputedStyle(parent);
    if (style.overflowX !== 'visible' || style.overflowY !== 'visible') clip = intersect(clip, parent.getBoundingClientRect());
  }
  if (clip.right <= clip.left || clip.bottom <= clip.top) return null;
  const radius = parseFloat(window.getComputedStyle(element).borderTopLeftRadius) || 0;
  return {
    sessionId: placeholder.sessionId, type: placeholder.type, fit: placeholder.fit, radius,
    x: rect.x, y: rect.y, width: rect.width, height: rect.height,
    clipX: clip.left, clipY: clip.top, clipWidth: clip.right - clip.left, clipHeight: clip.bottom - clip.top,
  };
}

function NativeVideoPlaceholder({ sessionId, type, fit, className }: NativeVideoPlaceholderProps) {
  const registry = useContext(RegistryContext);
  const element = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!registry || !element.current) return;
    return registry.register(element.current, { sessionId, type, fit });
  }, [registry, sessionId, type, fit]);
  // rounded-[inherit] lets the shell round the video to the tile's corners.
  return <div ref={element} data-native-video-hole="" data-testid={`native-video-${type}-${sessionId}`} className={cn(className, 'rounded-[inherit]')} />;
}

const renderPlaceholder = (props: NativeVideoPlaceholderProps) => <NativeVideoPlaceholder {...props} />;

/**
 * Shells with `webVideoTiles` draw each participant's video beneath the page, at the
 * rectangle of the web tile that would show it in a browser. The web tiles keep their own
 * names, badges and menus on top, so the app's call looks like the mobile web call.
 */
export function NativeVideoTiles({ enabled = true, state, visible, viewMode, pinnedId, onError, onViewportAcknowledged, children }: {
  /** Off for shells without the capability: the wrapper stays so the page below never remounts. */
  enabled?: boolean; state: NativeCallState; visible: boolean; viewMode: 'speaker' | 'grid'; pinnedId?: string;
  onError: (message: string) => void; onViewportAcknowledged?: (receipt: NativeViewportReceipt) => void; children: ReactNode;
}) {
  const placeholders = useRef(new Map<HTMLElement, Placeholder>());
  const schedule = useRef<(force?: boolean) => void>(() => undefined);
  const observer = useRef<ResizeObserver>();
  const registry = useMemo<Registry>(() => ({
    register: (element, placeholder) => {
      placeholders.current.set(element, placeholder);
      observer.current?.observe(element);
      schedule.current();
      return () => { placeholders.current.delete(element); observer.current?.unobserve(element); schedule.current(); };
    },
  }), []);
  const { meetingId, generation } = state;
  const settings = useRef({ visible, viewMode, pinnedId, onError, onViewportAcknowledged });
  settings.current = { visible, viewMode, pinnedId, onError, onViewportAcknowledged };
  const behind = useCallback((value: boolean) => document.documentElement.classList.toggle(NATIVE_VIDEO_BEHIND_CLASS, value), []);
  useLayoutEffect(() => {
    if (!enabled || !meetingId || generation === undefined) return;
    let frame = 0;
    let disposed = false;
    let forced = false;
    let sent = '';
    const update = (force: unknown = true) => {
      // Events that may follow a native geometry change invalidate older ACKs at once.
      if (force !== false) { forced = true; nextViewportRevision(); }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (disposed) return;
        const { visible: wanted, viewMode: mode, pinnedId: pinned } = settings.current;
        const shown = wanted && document.visibilityState === 'visible';
        const tiles = shown ? Array.from(placeholders.current, ([element, placeholder]) => measureNativeVideoTile(element, placeholder)).filter((tile): tile is NativeVideoTile => tile !== null).slice(0, MAX_NATIVE_VIDEO_TILES) : [];
        const payload = JSON.stringify([shown, mode, pinned, tiles]);
        // React re-renders on every roster update; only send geometry that changed.
        if (!forced && payload === sent) return;
        const revision = forced ? currentViewportRevision() : nextViewportRevision();
        forced = false; sent = payload;
        // Make the page opaque before the shell may hide its video; it becomes transparent only once the shell confirms.
        if (!tiles.length) behind(false);
        void nativeCall.viewport({ meetingId, generation }, { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight, visible: shown, viewMode: mode, pinnedId: pinned, tiles })
          .then(() => {
            if (disposed || revision !== currentViewportRevision()) return;
            behind(tiles.length > 0);
            if (!shown || document.visibilityState !== 'visible') return;
            const receipt = { meetingId, generation, revision };
            settings.current.onViewportAcknowledged?.(receipt);
            acknowledgeNativeViewport(receipt);
          })
          .catch(async (error: unknown) => {
            if (disposed || revision !== currentViewportRevision()) return;
            sent = '';
            behind(false);
            if (await viewportScopeRetired(error, meetingId, generation)) return;
            if (!disposed && revision === currentViewportRevision()) settings.current.onError('Unable to display the call. Close and reopen the call view.');
          });
      });
    };
    schedule.current = update;
    observer.current = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(() => update());
    placeholders.current.forEach((_, element) => observer.current?.observe(element));
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    document.addEventListener('visibilitychange', update);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    // A tile can move without resizing (a prejoin panel opening, a sidebar sliding in) from
    // state this wrapper never sees: re-measure after DOM changes and finished transitions.
    // Unchanged geometry is not resent.
    const moved = () => update(false);
    const mutations = typeof MutationObserver === 'undefined' ? undefined : new MutationObserver(moved);
    mutations?.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'open'] });
    document.addEventListener('transitionend', moved, true);
    document.addEventListener('animationend', moved, true);
    const stopRefresh = onNativeViewportRefresh(update);
    update();
    return () => {
      disposed = true; nextViewportRevision(); stopRefresh(); cancelAnimationFrame(frame);
      observer.current?.disconnect(); observer.current = undefined; schedule.current = () => undefined;
      mutations?.disconnect();
      document.removeEventListener('transitionend', moved, true); document.removeEventListener('animationend', moved, true);
      window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true);
      document.removeEventListener('visibilitychange', update);
      window.visualViewport?.removeEventListener('resize', update); window.visualViewport?.removeEventListener('scroll', update);
      behind(false);
      void nativeCall.viewport({ meetingId, generation }, { x: 0, y: 0, width: 0, height: 0, visible: false, viewMode: settings.current.viewMode, tiles: [] }).catch(() => undefined);
    };
  }, [enabled, meetingId, generation, behind]);
  // Layout changes that move no placeholder (opening a panel, a new tile) still need a fresh rectangle.
  useEffect(() => { schedule.current(false); });
  return <RegistryContext.Provider value={registry}><NativeVideoPlaceholderProvider value={renderPlaceholder}>{children}</NativeVideoPlaceholderProvider></RegistryContext.Provider>;
}
